import { Github } from 'lucide-react';
import type { PortfolioFeaturedProjectItem } from '../../firebase/types/portfolio.types';
import { lines } from './model';
import { CoverImage, LiveLink, ResultPill, UnavailableNotice } from './projectBits';
import { Band, SectionIndex } from './ui';

export default function FeaturedSection({
  projects,
  index,
}: {
  projects: PortfolioFeaturedProjectItem[];
  index: string;
}) {
  return (
    <Band id="featured">
      <SectionIndex index={index} title="Featured projects" />
      <div className="grid gap-3">
        {projects.map((project, i) => {
          const features = lines(project.keyFeatures);
          const name = project.name?.trim() || 'Featured project';
          const liveLabel = project.isNotWorking ? 'Live (may be down)' : 'Live';
          return (
            <article key={`${project.name ?? 'featured'}-${i}`} className="panel overflow-hidden">
              <CoverImage src={project.screenshotUrl} alt={name} />
              <div className="grid gap-6 p-5 md:p-6 lg:grid-cols-12">
                <div className="space-y-3 lg:col-span-5">
                  {project.name ? (
                    <h3 className="text-2xl font-semibold tracking-tight md:text-3xl">{project.name}</h3>
                  ) : null}
                  <ResultPill value={project.resultHighlight} />
                  <UnavailableNotice active={project.isNotWorking} />
                </div>
                <div className="space-y-4 text-sm leading-relaxed text-muted md:text-base lg:col-span-7">
                  {project.problem ? <p><span className="text-ink">Problem. </span>{project.problem}</p> : null}
                  {project.solution ? <p><span className="text-ink">Solution. </span>{project.solution}</p> : null}
                  {project.architectureThinking ? (
                    <p><span className="text-ink">Architecture. </span>{project.architectureThinking}</p>
                  ) : null}
                  {features.length ? (
                    <ul className="space-y-2">
                      {features.map((feature, j) => (
                        <li key={j} className="flex gap-3">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-signal" aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {(project.githubLink || project.liveLink) && (
                    <div className="flex flex-wrap gap-4 pt-2">
                      {project.githubLink ? (
                        <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="text-link inline-flex items-center gap-1.5">
                          <Github className="h-4 w-4" aria-hidden="true" />
                          GitHub
                        </a>
                      ) : null}
                      <LiveLink href={project.liveLink} label={liveLabel} />
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </Band>
  );
}
